import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { UserProfileLoader } from "../user/UserProfileLoader";
import { ConsentIds } from "./types/const";

export class ConsentManager {
    // #region Properties

    private eventBus: EventBus;

    private profileLoader: UserProfileLoader;

    // private apiUrl: string;

    // private options;

    // private userMail: string;

    /** Didomi User Profile receive from Front */
    private consentsStored: didomiUserProfile | null = null;

    // #endregion Properties

    /**
     * @constructor
     * Initialise un nouveau ConsentManager avec les dépendances nécessaires
     * ═══════════════════════════════════════════════════════════════════════════
     */
    constructor(eventBus: EventBus, profileLoader: UserProfileLoader) {
        logger.info([LOGS.ConsentManager], `ConsentManager...`);

        this.eventBus = eventBus;
        this.profileLoader = profileLoader;

        // this.apiUrl = DIDOMI_API_CONFIG[apiTarget];
        // this.userMail = userMail ?? 'unknown';

        // this.options = {
        //     retryAttempts: 3,
        // };
    }

    // #region Initialisation and cleanup

    public async _initialize(): Promise<void> {
        logger.info(
            [LOGS.ConsentManager, LOGS.ServiceProvider],
            "ConsentManager.initialize()",
        );

        // Ensure user profile is available before this component can be used
        await this.profileLoader.getUserProfile();
    }

    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the ConsentManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.ConsentManager,
            "ConsentManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.ConsentManager,
                "  Phase 1: Stopping active processes",
            );
        } catch (error) {
            logger.error(
                LOGS.ConsentManager,
                `ConsentManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        // Phase 2: Clear internal state and caches
        try {
            logger.info(
                LOGS.ConsentManager,
                "  Phase 2: Clearing internal state",
            );

            // Clear consent stored
            this.consentsStored = null;

            // Reset API URL to empty string
            // this.apiUrl = '';

            // Reset userMail to empty string
            // this.userMail = '';
        } catch (error) {
            logger.error(
                LOGS.ConsentManager,
                `ConsentManager.cleanup() - Error in Phase 2: ${error}`,
            );
        }

        logger.info(
            LOGS.ConsentManager,
            "ConsentManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Public Methods

    /**
     * hasAllRequiredConsents
     * Vérifie si tous les consentements requis ont été accordés par l'utilisateur.
     *
     * @param diomiUserProfile Profile créé dans Didomi
     * @param consentShouldBeCollected Envoié par le front. Sera utile lors de refacto du startup pour savoir si les consentements doivent être demandé
     * @returns Promise<boolean>
     */
    public async hasAllRequiredConsents(
        diomiUserProfile: didomiUserProfile,
        consentShouldBeCollected: boolean,
    ): Promise<boolean> {
        logger.info([LOGS.ConsentManager], `hasAllRequiredConsents()`);
        logger.info(
            [LOGS.ConsentManager],
            `hasAllRequiredConsents request details`,
            {
                diomiUserProfile: diomiUserProfile,
                consentShouldBeCollected: consentShouldBeCollected,
            },
        );

        // Check Si les consentements doivent être demandé
        if (consentShouldBeCollected) {
            logger.info(
                [LOGS.ConsentManager],
                `  => consents need to be collected (received consentShouldBeCollected = true)`,
            );
            return false;
        }

        try {
            // Stock en variable local le profile Didomi
            this.consentsStored = diomiUserProfile;

            // Check si tous les consentements requis sont OK
            const requiredConsents = [
                ConsentIds.CGU,
                ConsentIds.PRIVATE_POLICY,
                ConsentIds.ACCESS_MAIL_SERVICE,
            ];
            const hasAllRequiredConsents = requiredConsents.every((consentId) =>
                this._isConsentEnabled(diomiUserProfile, consentId),
            );

            logger.info(
                [LOGS.ConsentManager],
                ` => hasAllRequiredConsents : ${hasAllRequiredConsents}`,
            );

            // Si OK event pour maj feature flags + renvoie true pour le front
            if (hasAllRequiredConsents) {
                logger.info(
                    [LOGS.ConsentManager],
                    `  => no need to collect consents ( hasAllRequiredConsents = true)`,
                );
                this.eventBus.emit("user-consents-updated");
                return true;
            }
            // Si NOK renvoie false pour le front
            logger.info(
                [LOGS.ConsentManager],
                `  => consents need to be collected ( hasAllRequiredConsents = false)`,
            );
            return false;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error during save and check consents : ${errorMessage}`;
            logger.error(LOGS.ConsentManager, errorText);
            throw new Error(errorText);
        }
    }

    /**
     * isConsentGiven
     * Check si le consentement a été donné (ex: micro)
     *
     * @param consentId Id du consentement que l'on recherche
     * @returns Promise<boolean> - True if the consent is enabled, false otherwise
     */
    public async isConsentGiven(consentId: ConsentIds): Promise<boolean> {
        logger.info(LOGS.ConsentManager, `log isConsentGiven(${consentId})...`);

        // Validation des paramètres d'entrée
        if (!consentId || !Object.values(ConsentIds).includes(consentId)) {
            const errorText =
                "consentId is required and must be part of enum ConsentIds";
            logger.error(LOGS.ConsentManager, errorText);
            throw new Error(errorText);
        }

        if (!this.consentsStored) {
            logger.info(
                LOGS.ConsentManager,
                "  => No stored consent found, returning false",
            );
            return false;
            // throw new Error('consentsStored is not set');
        }

        try {
            // Recherche dans variable locale si le consentenment a été donné pour le consentId donné
            const foundPurpose: Purpose =
                this.consentsStored.purposes[consentId];
            logger.info(
                [LOGS.ConsentManager],
                `   => Consent found : ${JSON.stringify(foundPurpose)}`,
            );

            if (!foundPurpose) {
                logger.error(
                    [LOGS.ConsentManager],
                    `Consent purpose ${consentId} not found in ${this.consentsStored}`,
                );
                throw new Error(`Consent purpose ${consentId} not found`);
            }

            // Renvoie true/false en fonction du consentement de l'utilisateur
            const isConsentEnabled = this._isConsentEnabled(
                this.consentsStored,
                consentId,
            );
            logger.info(
                [LOGS.ConsentManager],
                `   => returning isConsentEnabled : ${isConsentEnabled}`,
            );

            return isConsentEnabled;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            const errorText = `Error check consent given: ${errorMessage}`;
            logger.error(LOGS.ConsentManager, errorText);
            throw new Error(errorText);
        }
    }

    // #endregion Public Methods

    // #region Private Methods

    /**
     * Checks if a specific consent purpose is enabled for the user.
     * @param didomiUserData - User's consent data from Didomi
     * @param consentId - The consent purpose ID to check
     * @returns True if the purpose is enabled, false otherwise
     */
    private _isConsentEnabled(
        didomiUserData: didomiUserProfile,
        consentId: ConsentIds,
    ): boolean {
        logger.info(
            [LOGS.ConsentManager],
            `Check if consent ${consentId} is enabled`,
        );
        return didomiUserData.purposes[consentId]?.enabled ?? false;
    }

    // #endregion Private Methods
}
